import { useEffect, useState } from 'react';
import { groupTripApi } from '../../api/groupTripApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';

function bodyOf(payload) {
  return payload?.data || payload || {};
}

export default function GroupTripRuntimeDecisions({ groupTripId }) {
  const { t, tf } = useTravelTranslation();
  const [board, setBoard] = useState(null);
  const [activities, setActivities] = useState([]);
  const [targetActivityId, setTargetActivityId] = useState('');
  const [proposedTitle, setProposedTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!groupTripId || typeof groupTripApi.listRuntimeDecisions !== 'function') return undefined;
    let cancelled = false;
    groupTripApi.listRuntimeDecisions(groupTripId)
      .then((payload) => {
        if (cancelled) return;
        const body = bodyOf(payload);
        setBoard({
          canCreate: Boolean(body.canCreate),
          decisions: Array.isArray(body.decisions) ? body.decisions : [],
        });
      })
      .catch(() => {
        if (!cancelled) setBoard(null);
      });
    return () => {
      cancelled = true;
    };
  }, [groupTripId, reloadToken]);

  useEffect(() => {
    if (!board?.canCreate || typeof groupTripApi.listJourneyActivities !== 'function') return undefined;
    let cancelled = false;
    groupTripApi.listJourneyActivities(groupTripId)
      .then((payload) => {
        if (cancelled) return;
        const items = bodyOf(payload).items || [];
        setActivities(Array.isArray(items) ? items.filter((item) => !item.cancelled) : []);
      })
      .catch(() => {
        if (!cancelled) setActivities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [board?.canCreate, groupTripId]);

  function run(action) {
    setBusy(true);
    return action()
      .then(() => setReloadToken((value) => value + 1))
      .finally(() => setBusy(false));
  }

  if (!board) return null;

  return (
    <section className="group-trips__panel" data-testid="group-trip-runtime-decisions">
      <h2>{t('travel.groupTrip.decisionTitle')}</h2>
      <p className="group-trips__hint">{t('travel.groupTrip.decisionHint')}</p>
      {board.canCreate ? (
        <form
          className="group-trips__actions"
          onSubmit={(event) => {
            event.preventDefault();
            if (!targetActivityId || !proposedTitle.trim()) return;
            run(() => groupTripApi.createRuntimeDecision(groupTripId, {
              targetActivityId,
              proposedTitle: proposedTitle.trim(),
            })).then(() => setProposedTitle('')).catch(() => {});
          }}
        >
          <label>
            {t('travel.groupTrip.decisionActivity')}
            <select
              value={targetActivityId}
              onChange={(event) => setTargetActivityId(event.target.value)}
              data-testid="runtime-decision-activity"
            >
              <option value="">{t('travel.groupTrip.decisionPickActivity')}</option>
              {activities.map((activity) => (
                <option key={activity.journeyActivityId} value={activity.journeyActivityId}>
                  {activity.title}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('travel.groupTrip.decisionProposed')}
            <input
              value={proposedTitle}
              maxLength={200}
              onChange={(event) => setProposedTitle(event.target.value)}
              data-testid="runtime-decision-proposed"
            />
          </label>
          <button type="submit" disabled={busy}>{t('travel.groupTrip.decisionCreate')}</button>
        </form>
      ) : null}
      {board.decisions.length === 0 ? <p className="group-trips__hint">{t('travel.groupTrip.decisionEmpty')}</p> : null}
      <ul className="group-trips__list">
        {board.decisions.map((decision) => (
          <li className="group-trips__card" key={decision.decisionId} data-testid={`runtime-decision-${decision.decisionId}`}>
            <p>{decision.sourceTitle} → {decision.proposedTitle}</p>
            <p>
              {tf('travel.groupTrip.decisionProgress', {
                cast: decision.castCount,
                eligible: decision.eligibleCount,
              })}
            </p>
            <p>
              {decision.proposedTitle}
              {'：'}
              {decision.acceptCount}
            </p>
            <p>
              {t('travel.groupTrip.decisionKeep')}
              {'：'}
              {decision.keepCount}
            </p>
            {decision.status === 'CLOSED' ? <p>{t('travel.groupTrip.decisionAwaiting')}</p> : null}
            {decision.status === 'CONFIRMED' ? <p>{t('travel.groupTrip.decisionConfirmed')}</p> : null}
            {decision.canVote ? (
              <div className="group-trips__actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => groupTripApi.voteRuntimeDecision(groupTripId, decision.decisionId, {
                    travelerId: decision.voterTravelerId,
                    optionId: decision.acceptOptionId,
                  }))}
                >
                  {decision.proposedTitle}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => groupTripApi.voteRuntimeDecision(groupTripId, decision.decisionId, {
                    travelerId: decision.voterTravelerId,
                    optionId: decision.keepOptionId,
                  }))}
                >
                  {t('travel.groupTrip.decisionKeep')}
                </button>
              </div>
            ) : null}
            {decision.voted && decision.status === 'OPEN' ? <p>{t('travel.groupTrip.decisionVoted')}</p> : null}
            {!decision.canVote && !decision.canClose && !decision.canConfirm && decision.status === 'OPEN' ? (
              <p>{t('travel.groupTrip.decisionNotEligible')}</p>
            ) : null}
            {decision.canClose ? (
              <div className="group-trips__actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => groupTripApi.closeRuntimeDecision(groupTripId, decision.decisionId))}
                >
                  {t('travel.groupTrip.decisionClose')}
                </button>
              </div>
            ) : null}
            {decision.canConfirm ? (
              <div className="group-trips__actions">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => run(() => groupTripApi.confirmRuntimeDecision(groupTripId, decision.decisionId))}
                >
                  {t('travel.groupTrip.decisionConfirm')}
                </button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
